import * as THREE from 'three';
import { SHARED_VERTEX_SHADER, SHARED_FRAGMENT_SHADER } from '../core/ParticleSystem.js';

/**
 * Workforce Scene - Human + AI augmentation visualization
 * Shows a human silhouette being augmented (not replaced) by AI
 */
export class WorkforceScene {
  constructor(count = 5000) {
    this.count = count;
    this.geometry = null;
    this.material = null;
    this.points = null;
    this.evolutionState = 0;

    this.init();
  }

  init() {
    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(this.count * 3);
    const colors = new Float32Array(this.count * 3);
    const sizes = new Float32Array(this.count);
    const phases = new Float32Array(this.count);

    // Create human silhouette points
    const humanCount = Math.floor(this.count * 0.6);

    for (let i = 0; i < humanCount; i++) {
      const i3 = i * 3;

      // Simple human silhouette shape
      const section = Math.random();
      let x, y;

      if (section < 0.15) {
        const angle = Math.random() * Math.PI * 2;
        const radius = Math.random() * 8;
        x = -15 + Math.cos(angle) * radius;
        y = 25 + Math.sin(angle) * radius;
      } else if (section < 0.5) {
        x = -15 + (Math.random() - 0.5) * 20;
        y = (Math.random() - 0.5) * 30;
      } else if (section < 0.75) {
        x = -30 + (Math.random() - 0.5) * 8;
        y = 5 + (Math.random() - 0.5) * 25;
      } else {
        x = 0 + (Math.random() - 0.5) * 8;
        y = 5 + (Math.random() - 0.5) * 25;
      }

      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = (Math.random() - 0.5) * 10;

      // Warm gold color for human
      colors[i3] = 1.0;
      colors[i3 + 1] = 0.84 + Math.random() * 0.1;
      colors[i3 + 2] = 0.0 + Math.random() * 0.2;

      // Wide size variation
      const randomVariation = 0.3 + Math.random() * 3.0;
      sizes[i] = (0.5 + Math.random() * 10.0) * randomVariation;
      phases[i] = Math.random() * Math.PI * 2;
    }

    // Create AI augmentation points
    for (let i = humanCount; i < this.count; i++) {
      const i3 = i * 3;

      const angle = Math.random() * Math.PI * 2;
      const distance = 25 + Math.random() * 30;

      positions[i3] = -15 + Math.cos(angle) * distance;
      positions[i3 + 1] = Math.sin(angle) * distance * 0.7;
      positions[i3 + 2] = (Math.random() - 0.5) * 20;

      // Cool blue color for AI
      colors[i3] = 0.29;
      colors[i3 + 1] = 0.5 + Math.random() * 0.2;
      colors[i3 + 2] = 1.0;

      // Wide size variation
      const randomVariation = 0.3 + Math.random() * 3.0;
      sizes[i] = (0.5 + Math.random() * 10.0) * randomVariation;
      phases[i] = Math.random() * Math.PI * 2;
    }

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

    this.uniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uSize: { value: 60 },
      uEvolution: { value: 0 }
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getFragmentShader(),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexColors: true
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  // Use shared shaders for consistent look
  getVertexShader() {
    return SHARED_VERTEX_SHADER;
  }

  getFragmentShader() {
    return SHARED_FRAGMENT_SHADER;
  }

  setEvolutionState(state) {
    this.evolutionState = Math.max(0, Math.min(1, state));
    this.uniforms.uEvolution.value = this.evolutionState;
  }

  update(time, deltaTime) {
    this.uniforms.uTime.value = time;
  }

  updateMouse(x, y) {
    this.uniforms.uMouse.value.set(x, y);
  }

  getMesh() {
    return this.points;
  }

  getPoints() {
    return this.points;
  }

  dispose() {
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }
}
